import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Edit, Ban, CheckCircle, Key, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/apiService';

const UsersManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    const isAdminAuth = localStorage.getItem('isAdminAuthenticated');
    if (!isAdminAuth) {
      navigate('/admin/login');
    } else {
      loadUsers();
    }
  }, [navigate]);

  const loadUsers = async () => {
    try {
      const data = await adminAPI.getAllUsers();
      setUsers(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
      setIsLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      currentTier: user.currentTier,
      status: user.status
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await adminAPI.updateUser(selectedUser._id || selectedUser.id, editFormData);
      await loadUsers();
      setIsEditDialogOpen(false);
      toast.success('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'blocked' : 'active';
    try {
      await adminAPI.updateUser(user._id || user.id, { status: newStatus });
      await loadUsers();
      toast.success(`User ${newStatus === 'blocked' ? 'blocked' : 'activated'} successfully!`);
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.firstName} ${user.lastName}"? This will permanently delete all their data including orders, transactions, and messages. This action cannot be undone.`)) {
      try {
        await adminAPI.deleteUser(user._id || user.id);
        await loadUsers();
        toast.success('User deleted successfully!');
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleSendResetPassword = async (user) => {
    if (window.confirm(`Send password reset email to ${user.firstName} ${user.lastName} (${user.email})?`)) {
      try {
        await adminAPI.sendResetPasswordEmail(user.email);
        toast.success(`Password reset email sent successfully to ${user.email}!`);
      } catch (error) {
        console.error('Error sending reset password email:', error);
        toast.error('Failed to send reset password email');
      }
    }
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        Active
      </Badge>
    ) : (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        Blocked
      </Badge>
    );
  };

  const getTierBadge = (tier) => {
    const colors = {
      1: 'bg-gray-100 text-gray-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-teal-100 text-teal-800'
    };
    const names = { 1: 'Starter', 2: 'Pro', 3: 'Elite' };
    return (
      <Badge className={`${colors[tier]} hover:${colors[tier]}`}>
        {names[tier]}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Users Management</h1>
              <p className="text-gray-700 text-lg font-medium">View and manage all registered users</p>
            </div>
            <Button 
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <Card className="border-3 border-red-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>All Users ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Lifetime Earnings</TableHead>
                    <TableHead className="text-center">Conversions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : users && users.length > 0 ? users.map((user) => (
                    <TableRow key={user._id || user.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        {user.firstName} {user.lastName}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="font-mono text-sm">{user.referralCode || 'N/A'}</TableCell>
                      <TableCell>{getTierBadge(user.currentTier || 'Starter')}</TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        ₹{(user.balance || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ₹{(user.lifetimeEarnings || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">{user.totalConversions || 0}</TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell>
                        {new Date(user.joinedDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-400 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEdit(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-purple-400 text-purple-600 hover:bg-purple-50"
                            onClick={() => handleSendResetPassword(user)}
                            title="Send Reset Password Email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className={`${user.status === 'active' ? 'border-red-400 text-red-600 hover:bg-red-50' : 'border-green-400 text-green-600 hover:bg-green-50'}`}
                            onClick={() => handleToggleStatus(user)}
                          >
                            {user.status === 'active' ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-600 text-red-700 hover:bg-red-100"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Edit User Details</DialogTitle>
              <DialogDescription>
                Update user information and tier settings
              </DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      value={editFormData.firstName}
                      onChange={(e) => setEditFormData({...editFormData, firstName: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      value={editFormData.lastName}
                      onChange={(e) => setEditFormData({...editFormData, lastName: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tier</Label>
                  <Select
                    value={editFormData.currentTier.toString()}
                    onValueChange={(value) => setEditFormData({...editFormData, currentTier: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Tier 1 - Starter (5%)</SelectItem>
                      <SelectItem value="2">Tier 2 - Pro (8%)</SelectItem>
                      <SelectItem value="3">Tier 3 - Elite (12%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editFormData.status}
                    onValueChange={(value) => setEditFormData({...editFormData, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    className="flex-1 text-white border-2 border-yellow-400"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                    onClick={handleSaveEdit}
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsEditDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default UsersManagement;
